/**
 * Con qué empresa trabaja ESTA ventana
 * -----------------------------------------------------------------------------
 * La sesión dice quién eres; la empresa la dice la ventana. Son dos cosas
 * distintas y hasta ahora vivían en el mismo sitio: la empresa se guardaba en la
 * sesión de cada aplicación, que es una por navegador. Con dos pestañas abiertas
 * -Pinkes en una, Owari en otra- las dos leían la última elegida, así que una de
 * las dos enseñaba datos que no eran los suyos sin decirlo.
 *
 * ## Por qué `sessionStorage`
 *
 * Porque es **por pestaña**. Dos ventanas, dos almacenes; se vacía al cerrarla y
 * una pestaña nueva abierta desde un enlace nace con el suyo. Es exactamente la
 * semántica que hace falta, sin inventar nada: `localStorage` lo comparte todo el
 * navegador y una cookie, todas las pestañas.
 *
 * ## Cómo entra y cómo se propaga
 *
 * Entra por la URL: el módulo que une la suite abre el marco con
 * `?owari_empresa=…`. De ahí se guarda una vez, y a partir de ese momento:
 *
 * - cada `fetch` sale con la cabecera `X-Owari-Empresa`;
 * - cada enlace del mismo origen que se pulsa se lleva el parámetro, así que la
 *   página siguiente vuelve a nacer con él y **una ventana nueva hereda la
 *   empresa del enlace**, que es lo que se espera al abrir algo en otra pestaña;
 * - lo mismo con la acción de los formularios.
 *
 * El servidor no se fía de nada de esto: comprueba en cada petición que quien
 * pregunta tenga acceso a esa empresa. Por eso puede ir a la vista en la URL -se
 * puede copiar y no concede nada-.
 *
 * ## Lo que NO hace
 *
 * No decide la empresa ni la cambia: solo repite la que le dieron. Cambiarla es
 * cosa del selector de la suite, que recarga el marco con otra. Y si no hay
 * ninguna -alguien entró a la aplicación directamente, sin la suite- no se
 * inventa una: se calla, y el servidor cae a su sesión y a su propio selector.
 */

/** El mismo nombre que usan la suite y todas las aplicaciones. */
const PARAMETRO = 'owari_empresa';

/** En la cabecera, para las llamadas de datos. */
const CABECERA = 'X-Owari-Empresa';

/**
 * La clave en `sessionStorage`. Con prefijo para no chocar con nada de la
 * aplicación que hospeda el tema.
 */
const CLAVE = 'owari.empresa_de_la_ventana';

let empresa = null;
let enganchado = false;

/**
 * En modo privado y con las cookies de terceros bloqueadas, `sessionStorage`
 * puede lanzar al tocarlo. No vale tumbar la página por esto: sin almacén, la
 * empresa dura lo que dure la página, y el servidor cae a la sesión como antes.
 */
function guardado() {
  try {
    return window.sessionStorage.getItem(CLAVE);
  } catch (error) {
    return null;
  }
}

function guardar(valor) {
  try {
    window.sessionStorage.setItem(CLAVE, valor);
  } catch (error) {
    /* Se sigue con el valor en memoria. */
  }
}

/**
 * Con qué empresa trabaja esta ventana, o `null` si nadie lo ha dicho.
 *
 * @returns {?string}
 */
export function empresaDeLaVentana() {
  return empresa;
}

/**
 * Le pega el parámetro a una dirección del mismo origen.
 *
 * Se respeta el que ya traiga: un enlace que apunta a otra empresa a propósito
 * -el selector de la suite- manda sobre lo que tenga guardado la pestaña.
 *
 * @param {string} url
 * @returns {string}
 */
export function conLaEmpresa(url) {
  if (!empresa || typeof url !== 'string' || url === '') return url;

  let destino;

  try {
    destino = new URL(url, window.location.href);
  } catch (error) {
    // Un `href` que no es una dirección -`#`, `javascript:`- se deja como está.
    return url;
  }

  // Solo al mismo origen. Mandarle el identificador de una empresa a otro
  // servidor sería contarle algo que no le hace falta.
  if (destino.origin !== window.location.origin) return url;

  if (destino.searchParams.has(PARAMETRO)) return url;

  destino.searchParams.set(PARAMETRO, empresa);

  return destino.toString();
}

/**
 * Envuelve `fetch` para que las llamadas al mismo origen lleven la cabecera.
 *
 * En cabecera y no en la dirección: así la respuesta guardada en caché no
 * depende de una URL con parámetros distintos, y las llamadas de datos quedan
 * legibles en el panel de red.
 */
function engancharFetch() {
  const original = window.fetch;

  if (typeof original !== 'function') return;

  window.fetch = function (entrada, opciones) {
    if (!empresa) return original.call(this, entrada, opciones);

    let url;

    try {
      url = new URL(
        typeof entrada === 'string' ? entrada : entrada.url,
        window.location.href
      );
    } catch (error) {
      return original.call(this, entrada, opciones);
    }

    if (url.origin !== window.location.origin) {
      return original.call(this, entrada, opciones);
    }

    // `Request` como primer argumento: se clona con la cabecera puesta. No se
    // puede mutar el original, sus cabeceras son de solo lectura.
    if (typeof entrada !== 'string' && entrada instanceof Request) {
      const copia = new Request(entrada, {});
      copia.headers.set(CABECERA, empresa);
      return original.call(this, copia, opciones);
    }

    const config = { ...(opciones || {}) };
    const cabeceras = new Headers(config.headers || {});
    cabeceras.set(CABECERA, empresa);
    config.headers = cabeceras;

    return original.call(this, entrada, config);
  };
}

/**
 * Los enlaces y los formularios, en captura y por delegación.
 *
 * Por delegación porque el HTML se repinta solo -las tablas se refrescan sin
 * recargar- y reescribir los `href` al arrancar dejaría fuera todo lo que llegue
 * después. En captura para llegar antes que cualquier manejador que decida
 * navegar por su cuenta.
 */
function engancharNavegacion() {
  document.addEventListener(
    'click',
    (evento) => {
      if (!empresa) return;

      const enlace = evento.target.closest && evento.target.closest('a[href]');
      if (!enlace) return;

      // Descargas y anclas no navegan a ninguna página nueva.
      if (enlace.hasAttribute('download')) return;

      const href = enlace.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      const conEmpresa = conLaEmpresa(enlace.href);
      if (conEmpresa !== enlace.href) enlace.href = conEmpresa;
    },
    true
  );

  document.addEventListener(
    'submit',
    (evento) => {
      if (!empresa) return;

      const formulario = evento.target;
      if (!formulario || formulario.tagName !== 'FORM') return;

      // Sin `action` el destino es la página actual, que ya la lleva.
      const acción = formulario.getAttribute('action');
      if (!acción) return;

      const conEmpresa = conLaEmpresa(formulario.action);
      if (conEmpresa !== formulario.action) formulario.action = conEmpresa;
    },
    true
  );
}

/**
 * Arranca la propagación. Idempotente: se puede llamar en cada `autoInit`.
 */
export function setupEmpresaDeLaVentana() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // La URL manda sobre lo guardado: es la que acaba de decir la suite, y lo
  // guardado puede ser de antes de cambiar de empresa en esta misma pestaña.
  let deLaUrl = null;

  try {
    deLaUrl = new URL(window.location.href).searchParams.get(PARAMETRO);
  } catch (error) {
    deLaUrl = null;
  }

  if (deLaUrl) {
    empresa = deLaUrl;
    guardar(deLaUrl);
  } else if (!empresa) {
    empresa = guardado();
  }

  if (enganchado) return;

  enganchado = true;
  engancharFetch();
  engancharNavegacion();
}

export default setupEmpresaDeLaVentana;
